import type { AxiosError } from 'axios'

/**
 * Telemetry disposition for a failed Axios response. Does not change
 * promise rejection — Axios always rejects; this only controls reporting.
 *
 * - `'ignore'`: emit no warning/error telemetry; still reject
 * - `'warning'`: emit warning-level telemetry; still reject
 * - `'error'`: emit error-level telemetry; still reject
 */
export type ApiErrorDisposition = 'ignore' | 'warning' | 'error'

/**
 * Per-request classifier. Return a disposition to override the default
 * policy, or `undefined` to fall back to `defaultErrorDisposition`.
 */
export type ClassifyApiError = (
  error: AxiosError
) => ApiErrorDisposition | undefined

export function defaultErrorDisposition(
  error: AxiosError
): ApiErrorDisposition {
  const status = error.response?.status
  if (status !== undefined && status >= 400 && status < 500) {
    return 'warning'
  }
  return 'error'
}

/**
 * Classifier for known empty-state endpoints where HTTP 404 means the
 * resource is absent by design. Returns `undefined` for other statuses.
 */
export const ignoreNotFound: ClassifyApiError = error =>
  error.response?.status === 404 ? 'ignore' : undefined
